import { FC, useEffect, useMemo } from 'react';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Box,
	TextField,
	FormControlLabel,
	Switch,
	CircularProgress,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
	createSalesChannelRequest,
	updateSalesChannelRequest,
	resetCreateSalesChannelSuccess,
	resetUpdateSalesChannelSuccess,
} from 'redux/reducers/shopifyStores/reducer';
import {
	selectCreateSalesChannelLoading,
	selectCreateSalesChannelSuccess,
	selectUpdateSalesChannelLoading,
	selectUpdateSalesChannelSuccess,
} from 'redux/reducers/shopifyStores/selectors';
import { TSalesChannelDialogProps } from './types';

const nullableString = Yup.string()
	.nullable()
	.transform((value) => (value === '' ? null : value));

const getValidationSchema = (isEdit: boolean) =>
	Yup.object({
		app_id: isEdit
			? Yup.number().integer('App ID must be an integer')
			: Yup.number()
					.transform((_value, originalValue) =>
						originalValue === '' || originalValue === null ? undefined : Number(originalValue),
					)
					.typeError('App ID must be a number')
					.required('App ID is required')
					.integer('App ID must be an integer'),
		title: isEdit
			? Yup.string().max(255, 'Max 255 characters')
			: Yup.string().required('Title is required').max(255, 'Max 255 characters'),
		shop_url: nullableString.max(191, 'Max 191 characters'),
		comment: nullableString,
		is_new: Yup.boolean(),
		is_active: Yup.boolean(),
	});

export const SalesChannelDialog: FC<TSalesChannelDialogProps> = ({
	open,
	onClose,
	salesChannelToEdit,
}) => {
	const dispatch = useDispatch();
	const isEdit = !!salesChannelToEdit;

	const createLoading = useSelector(selectCreateSalesChannelLoading);
	const createSuccess = useSelector(selectCreateSalesChannelSuccess);
	const updateLoading = useSelector(selectUpdateSalesChannelLoading);
	const updateSuccess = useSelector(selectUpdateSalesChannelSuccess);

	const loading = createLoading || updateLoading;
	const validationSchema = useMemo(() => getValidationSchema(isEdit), [isEdit]);

	const formik = useFormik({
		initialValues: {
			app_id: salesChannelToEdit?.app_id ?? ('' as const),
			title: salesChannelToEdit?.title ?? '',
			shop_url: salesChannelToEdit?.shop_url ?? '',
			comment: salesChannelToEdit?.comment ?? '',
			is_new: salesChannelToEdit?.is_new ?? true,
			is_active: salesChannelToEdit?.is_active ?? true,
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			const normalizedValues = {
				title: values.title,
				shop_url: values.shop_url || null,
				comment: values.comment || null,
				is_new: values.is_new,
				is_active: values.is_active,
			};

			if (isEdit && salesChannelToEdit) {
				dispatch(
					updateSalesChannelRequest({
						channel_id: salesChannelToEdit.id,
						app_id: Number(values.app_id),
						...normalizedValues,
					}),
				);
			} else {
				dispatch(
					createSalesChannelRequest({
						app_id: Number(values.app_id),
						...normalizedValues,
					}),
				);
			}
		},
	});

	useEffect(() => {
		if (createSuccess || updateSuccess) {
			dispatch(resetCreateSalesChannelSuccess());
			dispatch(resetUpdateSalesChannelSuccess());
			formik.resetForm();
			onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [createSuccess, updateSuccess]);

	const handleClose = () => {
		formik.resetForm();
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>{isEdit ? 'Edit Sales Channel' : 'Add Sales Channel'}</DialogTitle>
			<DialogContent>
				<Box
					component="form"
					onSubmit={formik.handleSubmit}
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
				>
					<TextField
						label="App ID"
						name="app_id"
						type="number"
						value={formik.values.app_id}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.app_id && !!formik.errors.app_id}
						helperText={formik.touched.app_id && formik.errors.app_id}
						size="small"
						disabled={isEdit}
					/>
					<TextField
						label="Title"
						name="title"
						value={formik.values.title}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.title && !!formik.errors.title}
						helperText={formik.touched.title && formik.errors.title}
						size="small"
					/>
					<TextField
						label="Shop URL"
						name="shop_url"
						value={formik.values.shop_url}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.shop_url && !!formik.errors.shop_url}
						helperText={formik.touched.shop_url && formik.errors.shop_url}
						size="small"
						disabled={isEdit}
					/>
					<TextField
						label="Comment"
						name="comment"
						value={formik.values.comment}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.comment && !!formik.errors.comment}
						helperText={formik.touched.comment && formik.errors.comment}
						size="small"
						multiline
						minRows={2}
					/>
					<FormControlLabel
						control={
							<Switch
								checked={formik.values.is_new}
								onChange={(e) => {
									void formik.setFieldValue('is_new', e.target.checked);
								}}
								name="is_new"
							/>
						}
						label="New"
					/>
					<FormControlLabel
						control={
							<Switch
								checked={formik.values.is_active}
								onChange={(e) => {
									void formik.setFieldValue('is_active', e.target.checked);
								}}
								name="is_active"
							/>
						}
						label="Active"
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={() => formik.handleSubmit()}
					variant="contained"
					color="primary"
					disabled={loading}
					startIcon={loading ? <CircularProgress size={16} /> : undefined}
				>
					{isEdit ? 'Save' : 'Create'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
